import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import {cx} from '../../util';

/** Ignore sub-pixel scroll remainders when deciding whether we're at the end. */
const EPSILON = 2;

/**
 * A scroll container that fades whichever edges still have content beyond
 * them. Conditional so short lists aren't dimmed; the pointer-events-none
 * overlays keep rows under them clickable.
 *
 * @param {string} [props.className] Applied to the scrolling element.
 * @param {string} [props.fadeClassName] Height/colour of the fade overlays.
 */
const ScrollFade = ({children, className, fadeClassName}) => {
  const scrollRef = useRef(null);
  const [showFade, setShowFade] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);

  const update = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const remaining = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowFade(remaining > EPSILON);
    setShowTopFade(el.scrollTop > EPSILON);
  }, []);

  useEffect(() => {
    update();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    // Watches both the viewport shrinking and the list itself changing height,
    // e.g. when menu sections appear for a coach or org admin.
    const observer = new ResizeObserver(update);
    observer.observe(el);
    if (el.firstElementChild) observer.observe(el.firstElementChild);
    return () => observer.disconnect();
  }, [update]);

  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        onScroll={update}
        className={cx('scrollbar-hidden h-full overflow-y-auto overflow-x-hidden', className)}
      >
        {children}
      </div>
      <div
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute inset-x-0 top-0 bg-gradient-to-b from-white via-white/80 to-transparent transition-opacity duration-200',
          fadeClassName || 'h-10',
          showTopFade ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-white via-white/80 to-transparent transition-opacity duration-200',
          fadeClassName || 'h-10',
          showFade ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  );
};

export default ScrollFade;
